<template>
    <div class="model-select" ref="root">
        <!-- Control: shows the resolved model, opens the picker -->
        <button type="button" class="form-control model-control"
            :class="{ 'is-open': open, 'is-custom': isCustomValue }" :disabled="disabled" @click="toggle"
            :aria-expanded="open" aria-haspopup="listbox">
            <span class="model-control-body">
                <span class="model-control-label">{{ selectedLabel }}</span>
                <span class="model-control-id" v-if="selectedSubLabel">{{ selectedSubLabel }}</span>
            </span>
            <span class="model-control-meta">
                <span class="badge model-badge model-badge-custom" v-if="isCustomValue">Custom</span>
                <i class="fa fa-chevron-down model-caret"></i>
            </span>
        </button>

        <!-- Picker -->
        <div class="model-panel" v-if="open">
            <div class="model-panel-search">
                <i class="fa fa-magnifying-glass"></i>
                <input ref="search" v-model="query" type="text" class="model-search-input"
                    placeholder="Search models, or type any model id" @keydown.esc.prevent="close"
                    @keydown.enter.prevent="commitTyped" @keydown.down.prevent="move(1)"
                    @keydown.up.prevent="move(-1)" />
                <button type="button" class="model-search-clear" v-if="query" @click="query = ''" title="Clear search">
                    <i class="fa fa-xmark"></i>
                </button>
            </div>

            <!-- Provider filter: chips beat a nested dropdown here because the
                 provider count is small and the filter is the primary way to cut
                 a long, near-identically-named model list down to size. -->
            <div class="model-panel-providers" v-if="providers.length > 1">
                <button type="button" class="provider-chip" :class="{ active: activeProvider === null }"
                    @click="activeProvider = null">
                    All
                    <span class="provider-count">{{ models.length }}</span>
                </button>
                <button v-for="p in providers" :key="p.name" type="button" class="provider-chip"
                    :class="{ active: activeProvider === p.name }"
                    @click="activeProvider = activeProvider === p.name ? null : p.name">
                    {{ p.name }}
                    <span class="provider-count">{{ p.count }}</span>
                </button>
            </div>

            <div class="model-panel-list" ref="list" role="listbox">
                <!-- Free-text entry: offered as a real option so an unlisted id
                     (new model, provisioned-throughput ARN) is a normal choice
                     rather than a workaround. Ids are unvalidated server-side. -->
                <button v-if="showTypedOption" type="button" class="model-option model-option-custom"
                    :class="{ pointed: pointer === -1 }" @click="commitTyped" @mouseenter="pointer = -1">
                    <span class="model-option-main">
                        <span class="model-option-label">Use "{{ trimmedQuery }}"</span>
                        <span class="model-option-sub">Custom model id — not in the discovered list</span>
                    </span>
                    <i class="fa fa-arrow-turn-down model-option-enter"></i>
                </button>

                <template v-for="(group, gi) in groupedResults" :key="group.provider">
                    <div class="model-group-header" v-if="groupedResults.length > 1 || gi === 0">
                        {{ group.provider }}
                    </div>
                    <button v-for="model in group.items" :key="model.id" type="button" class="model-option"
                        role="option" :aria-selected="model.id === modelValue"
                        :class="{ selected: model.id === modelValue, pointed: pointer === flatIndex(model) }"
                        @click="select(model.id)" @mouseenter="pointer = flatIndex(model)">
                        <span class="model-option-main">
                            <span class="model-option-label">
                                {{ model.label }}
                                <span class="badge model-badge model-badge-default" v-if="model.default">Service
                                    default</span>
                            </span>
                            <span class="model-option-sub">{{ model.id }}</span>
                        </span>
                        <span class="model-option-tags">
                            <!-- Flagged in the list as well as under the control:
                                 an advisory that only appears after selecting is
                                 an advisory the user reads too late. -->
                            <i class="fa fa-triangle-exclamation model-option-advisory" v-if="model.advisory"
                                :title="model.advisory"></i>
                            <span class="model-tag" v-if="model.kind === 'inference-profile'"
                                title="Cross-region inference profile">
                                Cross-region
                            </span>
                            <i class="fa fa-check model-option-check" v-if="model.id === modelValue"></i>
                        </span>
                    </button>
                </template>

                <div class="model-empty" v-if="!groupedResults.length && !showTypedOption">
                    No models match "{{ trimmedQuery }}".
                </div>
            </div>

            <div class="model-panel-footer">
                <span v-if="catalogSource === 'fallback'" class="text-warning">
                    <i class="fa fa-triangle-exclamation"></i>
                    Model discovery is unavailable. Type a model id to set one.
                </span>
                <span v-else-if="region">
                    Discovered in {{ region }}
                </span>
                <span v-else>&nbsp;</span>
            </div>
        </div>

        <!-- The selected model's advisory stays on screen after the picker
             closes: it is a standing property of the choice, not a hint about
             the act of choosing. -->
        <div class="model-advisory" v-if="selectedAdvisory">
            <i class="fa fa-triangle-exclamation model-advisory-icon"></i>
            <div class="model-advisory-body">
                <span class="model-advisory-text">{{ selectedAdvisory }}</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ModelSelect",
    props: {
        modelValue: { type: String, default: "" },
        models: { type: Array, default: () => [] },
        catalogSource: { type: String, default: null },
        region: { type: String, default: null },
        disabled: { type: Boolean, default: false },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            open: false,
            query: "",
            activeProvider: null,
            pointer: 0,
        };
    },
    computed: {
        trimmedQuery() {
            return this.query.trim();
        },
        selectedModel() {
            return this.models.find((m) => m.id === this.modelValue) || null;
        },
        isCustomValue() {
            return !!this.modelValue && !this.selectedModel;
        },
        selectedLabel() {
            if (!this.modelValue) return "Select a model";
            return this.selectedModel ? this.selectedModel.label : this.modelValue;
        },
        selectedSubLabel() {
            // Avoid echoing the id twice when the label already is the id.
            if (!this.selectedModel) return "";
            return this.selectedModel.label === this.modelValue ? "" : this.modelValue;
        },
        /**
         * A typed-in id has no catalog entry and so no advisory — silence there
         * is "unknown", not "cleared".
         */
        selectedAdvisory() {
            return this.selectedModel?.advisory || "";
        },
        /**
         * While the picker is open, follow the keyboard/hover pointer instead of
         * the committed value, so arrowing down a list previews each model's
         * advisory before anything is selected.
         */
        pointedAdvisory() {
            if (!this.open) return "";
            const pointed = this.pointer >= 0 ? this.flatList[this.pointer] : null;
            return pointed?.advisory || "";
        },
        providers() {
            const counts = new Map();
            this.models.forEach((m) => {
                const name = m.provider || "Unknown";
                counts.set(name, (counts.get(name) || 0) + 1);
            });
            return [...counts.entries()]
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        filtered() {
            const q = this.trimmedQuery.toLowerCase();
            return this.models.filter((m) => {
                if (this.activeProvider && (m.provider || "Unknown") !== this.activeProvider) return false;
                if (!q) return true;
                return (
                    m.label.toLowerCase().includes(q) ||
                    m.id.toLowerCase().includes(q) ||
                    (m.provider || "").toLowerCase().includes(q)
                );
            });
        },
        groupedResults() {
            const groups = new Map();
            this.filtered.forEach((m) => {
                const name = m.provider || "Unknown";
                if (!groups.has(name)) groups.set(name, []);
                groups.get(name).push(m);
            });
            return [...groups.entries()]
                .map(([provider, items]) => ({ provider, items }))
                .sort((a, b) => a.provider.localeCompare(b.provider));
        },
        flatList() {
            return this.groupedResults.flatMap((g) => g.items);
        },
        showTypedOption() {
            const q = this.trimmedQuery;
            if (!q) return false;
            // Only offer free text when it isn't already an exact known id.
            return !this.models.some((m) => m.id === q);
        },
    },
    watch: {
        query() {
            this.pointer = this.showTypedOption ? -1 : 0;
        },
    },
    methods: {
        toggle() {
            if (this.disabled) return;
            this.open ? this.close() : this.openPanel();
        },
        openPanel() {
            this.open = true;
            this.query = "";
            this.activeProvider = null;
            this.pointer = Math.max(0, this.flatList.findIndex((m) => m.id === this.modelValue));
            this.$nextTick(() => this.$refs.search?.focus());
        },
        close() {
            this.open = false;
            this.query = "";
        },
        select(id) {
            this.$emit("update:modelValue", id);
            this.close();
        },
        commitTyped() {
            if (this.pointer >= 0 && this.flatList[this.pointer]) {
                this.select(this.flatList[this.pointer].id);
                return;
            }
            if (this.trimmedQuery) this.select(this.trimmedQuery);
        },
        flatIndex(model) {
            return this.flatList.findIndex((m) => m.id === model.id);
        },
        move(delta) {
            const min = this.showTypedOption ? -1 : 0;
            const max = this.flatList.length - 1;
            if (max < min) return;
            let next = this.pointer + delta;
            if (next < min) next = max;
            if (next > max) next = min;
            this.pointer = next;
            this.$nextTick(() => {
                this.$refs.list?.querySelector(".model-option.pointed")?.scrollIntoView({ block: "nearest" });
            });
        },
        onDocumentClick(e) {
            if (this.open && this.$refs.root && !this.$refs.root.contains(e.target)) this.close();
        },
    },
    mounted() {
        document.addEventListener("click", this.onDocumentClick);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.onDocumentClick);
    },
};
</script>

<style scoped>
.model-select {
    position: relative;
}

.model-control {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    height: auto;
    min-height: 2.75rem;
    padding: 0.5rem 0.75rem;
    text-align: left;
    cursor: pointer;
}

.model-control:disabled {
    cursor: not-allowed;
    background-color: #edf2f9;
}

.model-control.is-open {
    border-color: #2c7be5;
    box-shadow: 0 0 0 3px rgba(44, 123, 229, 0.12);
}

.model-control-body {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
}

.model-control-label {
    font-size: 0.85rem;
    font-weight: 500;
    color: #12263f;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.model-control-id {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.7rem;
    color: #95aac9;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.model-control-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}

.model-caret {
    font-size: 0.7rem;
    color: #95aac9;
    transition: transform 0.15s ease;
}

.model-control.is-open .model-caret {
    transform: rotate(180deg);
}

.model-badge {
    font-size: 0.62rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    padding: 0.2rem 0.4rem;
}

.model-badge-custom {
    background-color: #fdf3e3;
    color: #d9822b;
}

.model-badge-default {
    background-color: #e3ebf6;
    color: #6e84a3;
    margin-left: 0.4rem;
}

/* Panel */
.model-panel {
    position: absolute;
    z-index: 1050;
    top: calc(100% + 0.35rem);
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #d2ddec;
    border-radius: 0.375rem;
    box-shadow: 0 1rem 2rem rgba(18, 38, 63, 0.14);
    overflow: hidden;
}

.model-panel-search {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 0.75rem;
    border-bottom: 1px solid #edf2f9;
    color: #95aac9;
}

.model-search-input {
    flex: 1;
    border: 0;
    outline: 0;
    font-size: 0.82rem;
    color: #12263f;
    background: transparent;
}

.model-search-clear {
    border: 0;
    background: transparent;
    color: #95aac9;
    padding: 0;
    line-height: 1;
}

.model-panel-providers {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    padding: 0.6rem 0.75rem;
    border-bottom: 1px solid #edf2f9;
}

.provider-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    border: 1px solid #e3ebf6;
    background: #f9fbfd;
    color: #6e84a3;
    border-radius: 999px;
    padding: 0.2rem 0.6rem;
    font-size: 0.72rem;
    font-weight: 500;
    transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.provider-chip:hover {
    border-color: #2c7be5;
    color: #2c7be5;
}

.provider-chip.active {
    background: #2c7be5;
    border-color: #2c7be5;
    color: #fff;
}

.provider-count {
    font-size: 0.65rem;
    opacity: 0.75;
}

.model-panel-list {
    max-height: 20rem;
    overflow-y: auto;
}

.model-group-header {
    position: sticky;
    top: 0;
    background: #f9fbfd;
    color: #95aac9;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 0.35rem 0.75rem;
    border-bottom: 1px solid #edf2f9;
}

.model-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #f4f7fb;
}

.model-option.pointed {
    background: #f0f5fc;
}

.model-option.selected {
    background: #e8f1fd;
}

.model-option-main {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
}

.model-option-label {
    font-size: 0.82rem;
    color: #12263f;
    font-weight: 500;
}

.model-option-sub {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.68rem;
    color: #95aac9;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.model-option-tags {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}

.model-tag {
    font-size: 0.62rem;
    color: #6e84a3;
    background: #edf2f9;
    border-radius: 0.25rem;
    padding: 0.15rem 0.4rem;
    white-space: nowrap;
}

.model-option-check {
    color: #2c7be5;
    font-size: 0.75rem;
}

.model-option-custom {
    background: #fffdf7;
}

.model-option-custom.pointed {
    background: #fdf3e3;
}

.model-option-enter {
    font-size: 0.7rem;
    color: #d9822b;
}

.model-empty {
    padding: 1rem 0.75rem;
    font-size: 0.8rem;
    color: #95aac9;
    text-align: center;
}

.model-panel-footer {
    padding: 0.45rem 0.75rem;
    border-top: 1px solid #edf2f9;
    background: #f9fbfd;
    font-size: 0.7rem;
    color: #95aac9;
}

/* Advisory */
.model-option-advisory {
    font-size: 0.72rem;
    color: #d9822b;
}

/* In-panel preview of the pointed model's advisory. Clamped so a long one
   cannot push the list out of reach. */
.model-advisory-inline {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    color: #d9822b;
    line-height: 1.45;
    max-height: 3.2rem;
    overflow-y: auto;
}

.model-advisory-inline i {
    margin-top: 0.1rem;
    flex-shrink: 0;
}

.model-advisory {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.5rem;
    padding: 0.55rem 0.7rem;
    border: 1px solid #f7e0bd;
    border-radius: 0.375rem;
    background: #fdf8ef;
}

.model-advisory-icon {
    font-size: 0.78rem;
    color: #d9822b;
    margin-top: 0.1rem;
    flex-shrink: 0;
}

.model-advisory-body {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
}

.model-advisory-text {
    font-size: 0.75rem;
    color: #a76420;
    line-height: 1.5;
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .model-control {
        background-color: $dark-input-bg !important;
        border-color: $dark-border-color !important;
    }

    .model-control:disabled {
        background-color: $dark-body-bg !important;
    }

    .model-control-label {
        color: $dark-text-primary !important;
    }

    .model-control-id,
    .model-caret {
        color: $dark-text-muted !important;
    }

    .model-panel {
        background: $dark-card-bg !important;
        border-color: $dark-border-color !important;
    }

    .model-panel-search,
    .model-panel-providers,
    .model-group-header,
    .model-panel-footer {
        border-color: $dark-border-color !important;
    }

    .model-group-header,
    .model-panel-footer {
        background: $dark-body-bg !important;
        color: $dark-text-muted !important;
    }

    .model-search-input {
        color: $dark-text-primary !important;
    }

    .provider-chip {
        background: $dark-input-bg !important;
        border-color: $dark-border-color !important;
        color: $dark-text-secondary !important;
    }

    .provider-chip.active {
        background: $dark-accent !important;
        border-color: $dark-accent !important;
        color: #fff !important;
    }

    .model-option {
        border-color: $dark-border-color !important;
    }

    .model-option.pointed {
        background: $dark-hover-bg !important;
    }

    .model-option.selected {
        background: $dark-selected-bg !important;
    }

    .model-option-label {
        color: $dark-text-primary !important;
    }

    .model-option-sub {
        color: $dark-text-muted !important;
    }

    .model-tag {
        background: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .model-option-custom {
        background: rgba(217, 130, 43, 0.08) !important;
    }

    .model-option-custom.pointed {
        background: rgba(217, 130, 43, 0.16) !important;
    }

    .model-badge-default {
        background-color: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .model-badge-custom {
        background-color: rgba(217, 130, 43, 0.18) !important;
        color: #e0a458 !important;
    }

    .model-option-advisory,
    .model-advisory-inline,
    .model-advisory-icon {
        color: #e0a458 !important;
    }

    .model-advisory {
        background: rgba(217, 130, 43, 0.1) !important;
        border-color: rgba(217, 130, 43, 0.32) !important;
    }

    .model-advisory-text {
        color: #e0a458 !important;
    }
}
</style>
